import React, { useMemo } from "react";
import {
  ConnectionProvider,
  WalletProvider,
} from "@solana/wallet-adapter-react";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from "@solana-mobile/wallet-adapter-mobile";
import { WalletConnectWalletAdapter } from "@walletconnect/solana-adapter";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { SOLANA_RPC, walletConnectV2ProjectId } from "@/config/sharedConfig";

// Import wallet adapter CSS
import "@solana/wallet-adapter-react-ui/styles.css";

interface SolanaWalletProviderProps {
  children: React.ReactNode;
}

export const SolanaWalletProvider: React.FC<SolanaWalletProviderProps> = ({
  children,
}) => {
  // Use custom RPC endpoint from config
  const endpoint = useMemo(() => SOLANA_RPC, []);

  // Configure supported wallets
  const wallets = useMemo(
    () => [
      new SolanaMobileWalletAdapter({
        addressSelector: createDefaultAddressSelector(),
        appIdentity: {
          name: "AINexus Marketplace",
          uri: typeof window !== "undefined" ? window.location.origin : "https://app.ainexus.com",
          icon: "/favicon.ico",
        },
        authorizationResultCache: createDefaultAuthorizationResultCache(),
        chain: WalletAdapterNetwork.Mainnet,
        onWalletNotFound: createDefaultWalletNotFoundHandler(),
      }),
      new WalletConnectWalletAdapter({
        network: WalletAdapterNetwork.Mainnet,
        options: {
          projectId: walletConnectV2ProjectId,
          metadata: {
            name: "AINexus Marketplace",
            description: "AINexus Marketplace - AI Model Trading Platform",
            url: typeof window !== "undefined" ? window.location.origin : "https://app.ainexus.com",
            icons: [typeof window !== "undefined" ? `${window.location.origin}/favicon.ico` : "https://app.ainexus.com/favicon.ico"],
          },
        },
      }),
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ],
    []
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>{children}</WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};
